// Единственное место, где живёт состояние приложения. Композиционный
// корень фронтенда: страницы получают готовые данные и ничего не грузят
// сами, поэтому источник (сервер или образец) виден в одном месте.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { BalanceConfig, DatasetInfo, Preset, RunRow } from '../lib/types';
import { summarize, findAnomalies, emptyAnomalyReport, type DatasetSummary, type AnomalyReport } from '../lib/stats';
import type { SimulateResult } from './source';
import {
  ApiError,
  loadDatasets,
  loadPresets,
  loadRuns,
  probeServer,
  simulate,
  type ServerState,
} from './source';

export interface SimulationState {
  /**
   * Сводка пересчёта ползунками. Строк прогонов сервер не отдаёт, поэтому
   * страницы, работающие по каждому seed, продолжают показывать датасет
   * и говорят об этом прямо.
   */
  result: SimulateResult | null;
  pending: boolean;
  error: string | null;
  config: BalanceConfig | null;
}

interface Store {
  server: ServerState | null;
  recheckServer: () => void;

  datasets: DatasetInfo[];
  datasetOrigin: 'server' | 'sample';
  dataset: string | null;
  setDataset: (name: string) => void;

  runs: RunRow[];
  runsOrigin: 'server' | 'sample';
  loading: boolean;
  error: string | null;

  presets: Preset[];

  /** Строки выбранного датасета: их видят страницы, работающие по seed. */
  active: RunRow[];
  activeLabel: string;
  /** Сводка: пересчёта ползунками, если он есть, иначе датасета. */
  summary: DatasetSummary;
  /** true — сводка пришла из пересчёта, а не из строк датасета. */
  summaryFromSimulation: boolean;
  anomalies: AnomalyReport;

  simulation: SimulationState;
  requestSimulation: (config: BalanceConfig, runs: number, seedFrom: number) => void;
  clearSimulation: () => void;
}

const StoreContext = createContext<Store | null>(null);

/** Какой датасет выбрать после загрузки списка: текущий, затем из адреса, затем первый. */
export function pickDataset(list: readonly { name: string }[], current: string | null, preferred: string | null): string | null {
  for (const candidate of [current, preferred]) {
    if (candidate && list.some((d) => d.name === candidate)) return candidate;
  }
  return list[0]?.name ?? null;
}

/** Задержка дребезга ползунка. Тысяча seed считается за 0.08 с — 300 мс хватает. */
export const SIMULATE_DEBOUNCE_MS = 300;
export const DEFAULT_SIMULATE_RUNS = 1000;
export const DEFAULT_SEED_FROM = 1;

/**
 * preferredDataset — датасет из адреса (`?dataset=`). Берётся при первой
 * загрузке списка, если такой датасет есть; иначе — первый, как раньше.
 * Ссылка с датасетом открывает тот же экран у второго человека.
 */
export function StoreProvider({ children, preferredDataset = null }: { children: ReactNode; preferredDataset?: string | null }) {
  const preferred = useRef(preferredDataset);
  preferred.current = preferredDataset;
  const [server, setServer] = useState<ServerState | null>(null);
  const [datasets, setDatasets] = useState<DatasetInfo[]>([]);
  const [datasetOrigin, setDatasetOrigin] = useState<'server' | 'sample'>('sample');
  const [dataset, setDataset] = useState<string | null>(null);
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [runsOrigin, setRunsOrigin] = useState<'server' | 'sample'>('sample');
  const [presets, setPresets] = useState<Preset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [simulation, setSimulation] = useState<SimulationState>({
    result: null,
    pending: false,
    error: null,
    config: null,
  });

  const probeTick = useRef(0);
  const [probeNonce, setProbeNonce] = useState(0);
  const recheckServer = useCallback(() => setProbeNonce((n) => n + 1), []);

  useEffect(() => {
    let alive = true;
    probeServer().then((state) => {
      if (alive) setServer(state);
    });
    return () => {
      alive = false;
    };
  }, [probeNonce]);

  useEffect(() => {
    if (server === null) return;
    let alive = true;
    const online = server.online;
    void (async () => {
      const [list, presetList] = await Promise.all([loadDatasets(online), loadPresets(online)]);
      if (!alive) return;
      setDatasets(list.value);
      setDatasetOrigin(list.origin);
      setPresets(presetList.value);
      setDataset((current) => pickDataset(list.value, current, preferred.current));
    })();
    return () => {
      alive = false;
    };
  }, [server]);

  useEffect(() => {
    if (server === null || dataset === null) return;
    let alive = true;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const result = await loadRuns(server.online, dataset);
        if (!alive) return;
        setRuns(result.value);
        setRunsOrigin(result.origin);
      } catch (failure) {
        if (!alive) return;
        setError(failure instanceof Error ? failure.message : String(failure));
        setRuns([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [server, dataset]);

  const debounce = useRef<number | null>(null);
  const requestSimulation = useCallback((config: BalanceConfig, count: number, seedFrom: number) => {
    if (debounce.current !== null) window.clearTimeout(debounce.current);
    setSimulation((s) => ({ ...s, pending: true, error: null, config }));
    const ticket = ++probeTick.current;
    debounce.current = window.setTimeout(() => {
      void (async () => {
        try {
          const result = await simulate({ config, runs: count, seedFrom });
          if (ticket !== probeTick.current) return;
          setSimulation({ result, pending: false, error: null, config });
        } catch (failure) {
          if (ticket !== probeTick.current) return;
          const message =
            failure instanceof ApiError
              ? failure.message
              : failure instanceof Error
                ? failure.message
                : String(failure);
          setSimulation({ result: null, pending: false, error: message, config });
        }
      })();
    }, SIMULATE_DEBOUNCE_MS);
  }, []);

  const clearSimulation = useCallback(() => {
    if (debounce.current !== null) window.clearTimeout(debounce.current);
    probeTick.current++;
    setSimulation({ result: null, pending: false, error: null, config: null });
  }, []);

  const active = runs;
  const datasetSummary = useMemo(() => summarize(active), [active]);
  const summary = simulation.result?.summary ?? datasetSummary;
  const anomalies = useMemo(
    () => (active.length > 0 ? findAnomalies(active, datasetSummary) : emptyAnomalyReport()),
    [active, datasetSummary],
  );

  const value: Store = {
    server,
    recheckServer,
    datasets,
    datasetOrigin,
    dataset,
    setDataset,
    runs,
    runsOrigin,
    loading,
    error,
    presets,
    active,
    activeLabel: simulation.result
      ? `пересчёт: ${simulation.config?.name ?? 'без имени'}`
      : (dataset ?? '—'),
    summary,
    summaryFromSimulation: simulation.result !== null,
    anomalies,
    simulation,
    requestSimulation,
    clearSimulation,
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore(): Store {
  const store = useContext(StoreContext);
  if (store === null) throw new Error('useStore вызван вне StoreProvider');
  return store;
}
